import { executeRequest } from '@/core/either';
import { http } from '@/core/api';
import {
  API_VERSION_HEADER,
  API_VERSION_MOVEMENTS,
} from '@/shared/common/constants/apiVersion.constant';
import type { ApiResult } from '@/core/either';
import type { AxiosProgressEvent } from 'axios';

// Types
import type {
  TypeFundsSources,
  TypeFundsSourcesData,
} from '@/types/FundsSource';
import type { TypeKushkiBanks, TypeKushkiResponse, TypeWompiBanks } from '@/types/PaymentTypes';
import type {
  PaymentBanksByBranch,
  PaymentBanksByBranchParams,
} from '@/api/types/cashin/PaymentMethods';
import type { QuoteResponse } from '@/types/QuoteType';
import type { InboundBank } from '@/types/TransactionType';
import type PaymentType from '@/types/PaymentTypes';
import type { PaymentObject } from '@/types/PaymentObject';
import type { PayInResponse } from '@/types/PayInResponse';
import type { DirectDeposit } from '@/types/DirectDepositType';
import type { PayvalidaPaymentRequest, PayValidaResponse } from '@/api/types/cashin/PayValidaTypes';
import type { WompiTermsResponse, WompiTransactionStatusResponse } from '../types/wompi.type';
import type {
  InswitchB2bTransactionResponse,
  InswitchPaymentPayload,
} from '@/shared/inswitch/types/inswitch.type';
import type { ProductGatewayMovementsFirstPageResponse } from '../types/productGatewayCashIn.type';

export const cashInService = {
  /**
   * Crea una notificación de depósito subiendo archivos de comprobante.
   * @param params - Parámetros para crear la notificación.
   * @param params.files - FormData con archivos de comprobante.
   * @param params.progressCallback - Callback opcional para reportar progreso de subida.
   * @returns Promesa Either ApiResult<void>.
   */
  createDepositNotification(
    params: {
      files: FormData;
      progressCallback?: (progressEvent: AxiosProgressEvent) => void;
    }
  ): Promise<ApiResult<void>> {
    return executeRequest(() =>
      http.post<void>('product-gateway/b2b/transaction/notify', params.files, {
        onUploadProgress: params.progressCallback || (() => null),
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
    );
  },

  /**
   * Crea y envía voucher por email para transferencias interbancarias.
   * @param params - Parámetros para crear el voucher.
   * @param params.files - FormData con archivos del voucher.
   * @param params.progressCallback - Callback opcional para reportar progreso de subida.
   * @returns Promesa Either ApiResult<void>.
   */
  createVoucherInterbank(
    params: {
      files: FormData;
      progressCallback?: (progressEvent: AxiosProgressEvent) => void;
    }
  ): Promise<ApiResult<void>> {
    return executeRequest(() =>
      http.post<void>('transaction/b2b/send-voucher-email', params.files, {
        onUploadProgress: params.progressCallback || (() => null),
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
    );
  },

  /**
   * Obtiene la lista de fuentes de fondos disponibles.
   * @returns Promesa Either ApiResult<TypeFundsSources[]>.
   */
  getFundsSources(): Promise<ApiResult<TypeFundsSources[]>> {
    return executeRequest(() =>
      http.get<TypeFundsSources[]>('transaction/b2b/fund-sources')
    );
  },

  /**
   * Crea una nueva fuente de fondos.
   * @param fund - Datos de la fuente de fondos a crear.
   * @returns Promesa Either ApiResult<TypeFundsSources>.
   */
  createFundsSource(
    fund: TypeFundsSourcesData
  ): Promise<ApiResult<TypeFundsSources>> {
    return executeRequest(() =>
      http.post<TypeFundsSources>('transaction/b2b/fund-sources', fund)
    );
  },

  /**
   * Obtiene bancos disponibles por sucursal para pagos.
   * @param params - Parámetros de búsqueda de bancos.
   * @returns Promesa Either ApiResult<PaymentBanksByBranch[]>.
   */
  getPaymentBanksByBranch(
    params: PaymentBanksByBranchParams
  ): Promise<ApiResult<PaymentBanksByBranch[]>> {
    return executeRequest(() =>
      http.get<PaymentBanksByBranch[]>('treasury/b2b/business-bank', { params })
    );
  },

  /**
   * Crea un pago vía gateway para cash-in.
   * El endpoint devuelve diferentes tipos según el método de pago:
   * - PayValida (PEN): PayValidaResponse
   * - DLocal (USD): PayInResponse
   * @param data - Datos del pago (PaymentObject | PayvalidaPaymentRequest).
   * @returns Promesa Either ApiResult<PayValidaResponse | PayInResponse>.
   */
  createGatewayPayment(data: PaymentObject | PayvalidaPaymentRequest): Promise<ApiResult<PayValidaResponse | PayInResponse>> {
    return executeRequest(() =>
      http.post<PayValidaResponse | PayInResponse>('product-gateway/b2b/payments/pay-in', data)
    );
  },

  /**
   * Obtiene la lista de bancos disponibles para Kushki.
   * @returns Promesa Either ApiResult<TypeKushkiBanks[]>.
   */
  getKushkiBankList(): Promise<ApiResult<TypeKushkiBanks[]>> {
    return executeRequest(() =>
      http.get<TypeKushkiBanks[]>('payment/b2b/kushki/banks')
    );
  },

  /**
   * Obtiene los datos de una transferencia Kushki usando el token.
   * @param token - Token de la transferencia Kushki.
   * @returns Promesa Either ApiResult<TypeKushkiResponse>.
   */
  getKushkiTransferData(token: string): Promise<ApiResult<TypeKushkiResponse>> {
    return executeRequest(() =>
      http.get<TypeKushkiResponse>(`payment/b2b/kushki/details/${token}`)
    );
  },

  /**
   * Obtiene la lista de bancos disponibles para Wompi PSE.
   * @returns Promesa Either ApiResult<TypeWompiBanks[]>.
   */
  getWompiBanks(): Promise<ApiResult<TypeWompiBanks[]>> {
    return executeRequest(() =>
      http.get<TypeWompiBanks[]>('wompi/b2b/banks')
    );
  },

  /**
   * Obtiene los términos y condiciones de Wompi.
   * @returns Promesa Either ApiResult con presignedAcceptance y presignedPersonalDataAuth.
   */
  getWompiTerms(): Promise<ApiResult<WompiTermsResponse>> {
    return executeRequest(() =>
      http.get<WompiTermsResponse>('wompi/b2b/terms-conditions')
    );
  },

  /**
   * Obtiene los métodos de pago disponibles para un cliente.
   * @param params - Parámetros para obtener métodos de pago.
   * @param params.routeId - ID de la ruta.
   * @param params.originAmountUSD - Monto en USD de origen.
   * @returns Promesa Either ApiResult<PaymentType[]>.
   */
  getCustomerPaymentMethod(
    params: { routeId: number; originAmountUSD: number }
  ): Promise<ApiResult<PaymentType[]>> {
    return executeRequest(() =>
      http.get<PaymentType[]>('route/b2b/payment-methods', {
        params: {
          routeId: params.routeId,
          customerTypeB2B: true,
          originAmountUSD: params.originAmountUSD,
        },
      })
    );
  },

  /**
   * Obtiene el monto equivalente en USD.
   * @param params - Parámetros para la conversión.
   * @param params.routeId - ID de la ruta.
   * @param params.amount - Monto a convertir.
   * @returns Promesa Either ApiResult<QuoteResponse>.
   */
  getAmountInUSD(
    params: { routeId: string; amount: number }
  ): Promise<ApiResult<QuoteResponse>> {
    return executeRequest(() =>
      http.get<QuoteResponse>('quote/b2b/dollars', {
        params: { routeId: params.routeId, amount: params.amount },
      })
    );
  },

  /**
   * Crea un pago con socio (partner cash-in).
   * @param paymentObject - Objeto con datos del pago.
   * @returns Promesa Either ApiResult<PayInResponse>.
   */
  createPartnerCashIn(
    paymentObject: PaymentObject
  ): Promise<ApiResult<PayInResponse>> {
    return executeRequest(() =>
      http.post<PayInResponse>('payment/b2b/pay-in', paymentObject, {
        headers: {
          'X-API-VERSION': '2',
        },
      })
    );
  },

  /**
   * Crea un depósito directo.
   * @param transactionId - ID de la transacción.
   * @param bankData - Datos del banco.
   * @returns Promesa Either ApiResult<DirectDeposit>.
   */
  createDeposit(
    transactionId: string | undefined,
    bankData: InboundBank
  ): Promise<ApiResult<DirectDeposit>> {
    return executeRequest(() =>
      http.put<DirectDeposit>(
        `transaction/b2b/deposits/${transactionId}`,
        bankData
      )
    );
  },

  /**
   * Obtiene la URL encriptada de redirección para Wompi.
   * @param params - Parámetros para obtener la URL de redirección.
   * @param params.paymentId - ID del pago obtenido del PayIn.
   * @param params.iv - Vector de inicialización obtenido del PayIn (Base64).
   * @returns Promesa Either ApiResult con la URL encriptada en redirectUrl.
   */
  getWompiRedirectUrl(params: {
    paymentId: string | number;
    iv: string;
  }): Promise<ApiResult<{ url: string }>> {
    return executeRequest(() =>
      http.get<{ url: string }>('payment/b2b/wompi/redirect', {
        params: {
          paymentId: params.paymentId,
          iv: params.iv,
        },
      })
    );
  },

  /**
   * Obtiene el estado de una transacción Wompi usando el código de referencia.
   * @param referenceCode - Código de referencia obtenido del PayIn response.
   * @returns Promesa Either ApiResult con el estado de la transacción.
   */
  getWompiTransactionStatus(
    referenceCode: string
  ): Promise<ApiResult<WompiTransactionStatusResponse>> {
    return executeRequest(() =>
      http.get<WompiTransactionStatusResponse>('wompi/b2b/transaction', {
        params: {
          referenceCode,
        },
      })
    );
  },

  /**
   * Crea un pago InSwitch (pay-in con X-API-VERSION 3).
   * @param paymentObject - Objeto con datos del pago InSwitch incluyendo errorCallback.
   * @returns Promesa Either ApiResult<PayInResponse>.
   */
  createInswitchPayIn(
    paymentObject: InswitchPaymentPayload
  ): Promise<ApiResult<PayInResponse>> {
    return executeRequest(() =>
      http.post<PayInResponse>('payment/b2b/pay-in', paymentObject, {
        headers: {
          'X-API-VERSION': '3',
        },
      })
    );
  },

  /**
   * Obtiene el estado de una transacción InSwitch por referencia (B2B).
   * @param transactionReference - Referencia de la transacción (path).
   * @returns Promesa Either ApiResult con transactionStatus (p. ej. finished, cancelled).
   */
  getInswitchB2bTransactionByReference(
    transactionReference: string
  ): Promise<ApiResult<InswitchB2bTransactionResponse>> {
    const encoded = encodeURIComponent(transactionReference);
    return executeRequest(() =>
      http.get<InswitchB2bTransactionResponse>(
        `inswitch/b2b/transactions/${encoded}`
      )
    );
  },

  /**
   * Primera página de movimientos del product-gateway (cash-in por moneda), en la versión 2 del endpoint.
   * @param params - Parámetros de filtrado.
   * @param params.currencyCode - Código de moneda (ej. COP, CLP, USD).
   * @returns Promesa Either ApiResult<ProductGatewayMovementsFirstPageResponse>.
   */
  getProductGatewayMovementsFirstPage(params: {
    currencyCode: string;
  }): Promise<ApiResult<ProductGatewayMovementsFirstPageResponse>> {
    return executeRequest(() =>
      http.get<ProductGatewayMovementsFirstPageResponse>(
        'product-gateway/b2b/transaction/movements',
        {
          params: {
            page: 1,
            currencyCode: params.currencyCode,
          },
          headers: { [API_VERSION_HEADER]: API_VERSION_MOVEMENTS },
        }
      )
    );
  },

};
